import React, { useState, useRef, useEffect } from "react";
import {
  Grid,
  TextField,
  CircularProgress,
  Autocomplete,
  InputAdornment,
  CurrencyInput,
  TextareaAutosize,
  Divider,
} from "../../../components";
import {
  ButtonMenu,
  TabsMdfe,
  ErrosMdfeDialog,
  CancelamentoDialog,
} from "./components";
import { AddLayout } from "../../../layouts";
import { InputErros, value, PermisoesHelper } from "../../../helpers";
import {
  userLogs,
  responseErros,
  formatPlacaVeiculo,
  cacheApi,
  voltar,
  formatDate,
  formatPrecisaoFloat,
} from "../../../utils";
import {
  VeiculosService,
  EnderecoService,
  MdfeService,
  notification,
  SessionService,
  OperacaoFiscalService,
  history,
} from "../../../services";
import { useCacheContext } from "../../../contexts/cache.context";
import { MdfeEntity } from "./entities";
import { useImportContext } from "../../../contexts/import.context";
import { mdfeValidator, mdfeUpdateValidator } from "./middlewares";
import { format } from "date-fns";

const permissoesHelper = new PermisoesHelper();
const veiculosService = new VeiculosService();
const enderecoService = new EnderecoService();
const mdfeService = new MdfeService();
const sessionService = new SessionService();
const operacaoFiscalService = new OperacaoFiscalService();

const AddMdfeView = ({ match }) => {
  const { id } = match.params;
  const [loading, setLoading] = useState(false);
  const [userLog, setUserLog] = useState({});
  const [abaValue, setAbaValue] = useState(0);
  const [errosMdfeDialogOpen, setErrosMdfeDialogOpen] = useState(false);
  const [openCancelamentoDialog, setOpenCancelamentoDialog] = useState(false);
  const [errosMdfe, setErrosMdfe] = useState(null);
  const [mdfe, setMdfe] = useState({});
  const [valorTotal, setValorTotal] = useState(0);
  const [pesoTotal, setPesoTotal] = useState(0);
  const [percursos, setPercursos] = useState([]);
  const [contratantes, setContratantes] = useState([]);
  const [ciots, setCiots] = useState([]);
  const [carregamentos, setCarregamentos] = useState([]);
  const [nfsSaida, setNfsSaida] = useState([]);
  const [condutoresMdfe, setCondutoresMdfe] = useState([]);
  const [reboques, setReboques] = useState([]);
  const [estados, setEstados] = useState([]);
  const [loadingAutoComplete, setLoadingAutoComplete] = useState(false);
  const [inputErro, setInputErro] = useState([]);
  const [lacres, setLacres] = useState([]);
  const [pedagios, setPedagios] = useState([]);
  const inputErros = new InputErros(inputErro, setInputErro);
  const [veiculos, setVeiculos] = useState([]);
  const { cacheDto, setCacheDto } = useCacheContext();
  const inputVeiculos = useRef();
  const { initialCollapses } = useImportContext();
  permissoesHelper.validarPermisao("mdfe-visualizar");

  useEffect(() => {
    buscarDadosIniciais();
  }, []);

  function buscarDadosIniciais() {
    buscarMdfe();
    buscaEstados();
    buscarOperacaoFiscalPadrao();
  }

  function buscarOperacaoFiscalPadrao() {
    if (id) return;
    const dadosEmpresa = sessionService.getEmpresaAndUsuario();
    if (!dadosEmpresa.empresa.configuracoes.operacaoFiscalMdfePadraoId) return;
    operacaoFiscalService
      .getById(dadosEmpresa.empresa.configuracoes.operacaoFiscalMdfePadraoId)
      .then((result) => {
        if (!result.isAxiosError) {
          setMdfe({
            serieDocumento: result.data?.serieDocumento,
          });
        }
      });
  }

  const buscarMdfe = async () => {
    if (!id) {
      return;
    }
    setLoading(true);
    const res = await mdfeService.getById(id);
    setLoading(false);
    if (!res.isAxiosError) {
      setMdfe(res.data);
      montarNfsSaidas(res.data.mdfeNfsSaida);
      montarReboques(res.data.reboques);
      setCondutoresMdfe(res.data.condutores);
      setCarregamentos(res.data.municipiosCarregamento);
      setPercursos(res.data.percursos);
      setCiots(res.data.ciots);
      setContratantes(res.data.contratantes);
      setLacres(res.data.lacres);
      setPedagios(res.data.pedagios);
    } else {
      responseErros(res);
    }
  };

  const calcularTotaisNfsSaida = (nfsSaidaLista) => {
    let pesoTotal = 0;
    let valorTotal = 0;
    nfsSaidaLista.forEach((nf) => {
      pesoTotal += parseInt(nf.pesoTotal);
      valorTotal = formatPrecisaoFloat(valorTotal + nf.valorTotal);
    });
    setValorTotal(valorTotal);
    setPesoTotal(pesoTotal);
  };

  function montarReboques(mdfeReboques) {
    if (mdfeReboques && mdfeReboques.length) {
      setReboques(
        mdfeReboques.map((reboque) => {
          return { ...reboque.veiculo, id: reboque.id };
        })
      );
    }
  }

  const calcularValorTotalNfSaida = (notaFiscalSaida) => {
    const totalItens = notaFiscalSaida.nfSaidaItens.reduce(
      (totais, itens) =>
        formatPrecisaoFloat(totais + itens.valor * itens.quantidade),
      0
    );
    return totalItens + notaFiscalSaida.acrescimo - notaFiscalSaida.desconto;
  };

  function montarNfsSaidas(mdfeNfsSaida) {
    const mdfeNfsSaidaTemp = mdfeNfsSaida.map((mdfeNfSaida) => {
      return {
        id: mdfeNfSaida.id,
        codigoNfSaida: mdfeNfSaida?.nfsSaida?.numeroDocumento,
        fatNfSaidaId: mdfeNfSaida.fatNfSaidaId,
        cidadeId: mdfeNfSaida.cidadeId,
        codigo: mdfeNfSaida.codigo,
        nome: mdfeNfSaida.nome,
        uf: mdfeNfSaida.uf,
        pesoTotal: mdfeNfSaida.pesoTotal,
        valorTotal: mdfeNfSaida.fatNfSaidaId
          ? calcularValorTotalNfSaida(mdfeNfSaida.nfsSaida)
          : mdfeNfSaida.valorTotal,
        chaveDfe: mdfeNfSaida?.chaveDfe,
      };
    });
    calcularTotaisNfsSaida(mdfeNfsSaidaTemp);
    setNfsSaida(mdfeNfsSaidaTemp);
  }

  const buscaEstados = async () => {
    const cacheBody = {
      timeMinutes: 60,
      apiFunction: enderecoService.getEstados(),
      dto: { cacheDto: cacheDto, setCacheDto: setCacheDto },
    };
    const res = await cacheApi(`${enderecoService.urlBase}/estados`, cacheBody);
    if (!res.isAxiosError) {
      setEstados(res.data);
    } else {
      responseErros(res);
    }
  };

  async function buscarVeiculos(filter) {
    const result = await veiculosService.getAll(filter);
    if (!result.isAxiosError) {
      setVeiculos(result.data);
    } else {
      responseErros(result);
    }
  }

  const verificarStatus = (status) => {
    switch (status) {
      case "PENDENTE":
        return (
          <i className="ph-fill ph-circle" style={{ color: "#EA9D2B" }}></i>
        );
      case "ENVIADA":
        return (
          <i className="ph-fill ph-circle" style={{ color: "#4FBB53" }}></i>
        );
      case "CANCELADA":
        return (
          <i className="ph-fill ph-circle" style={{ color: "#DC3545" }}></i>
        );
      case "ENCERRADA":
        return (
          <i className="ph-fill ph-circle" style={{ color: "#004780" }}></i>
        );
    }
  };

  async function onClickLog() {
    if ("userCreatedLog" in userLog) return;
    const logs = await userLogs(mdfe);
    setUserLog(logs);
  }

  async function emitirMdfe(idMdfe) {
    setLoading(true);
    const response = await mdfeService.emitirNotaFiscal(idMdfe);
    setLoading(false);
    return alertaRetornoEmitirNota(response, idMdfe);
  }

  function alertaRetornoEmitirNota(response, idMdfe = null) {
    if (!response.isAxiosError) {
      notification.sucessoGenericos("Nota Emitida com sucesso");
      return true;
    }
    const res = response.response;
    if (res.status === 500 && res.data?.message) {
      handleDialogErrorMdfe(res.data.message, 500, idMdfe);
    } else if (res.status === 400 && res.data?.message) {
      handleDialogErrorMdfe(res.data.message, 400, idMdfe);
    } else {
      notification.alertaGenericos(response.mensagem);
      if (!id) {
        history.push(`/faturamento/mdfe/${idMdfe}`);
      }
    }
    return false;
  }

  function handleDialogErrorMdfe(mensagem, status, idMdfe) {
    setErrosMdfe({ mensagem, status, idMdfe });
    setErrosMdfeDialogOpen(true);
  }

  const onClickCancelarMdfe = () => {
    setOpenCancelamentoDialog(true);
  };

  const onClickEmitirMdfe = async () => {
    const notaEmitida = await emitirMdfe(id);
    if (notaEmitida) await buscarMdfe();
  };

  const onClickEncerrarMdfe = async () => {
    setLoading(true);
    const response = await mdfeService.encerrar(id);
    setLoading(false);
    if (!response.isAxiosError) {
      notification.alteracaoSucesso();
      await buscarMdfe();
      return;
    }
    if (response.status != 400) {
      responseErros(response);
    }
  };

  const onClickVisualizarMdfe = async () => {
    setLoading(true);
    const result = await mdfeService.getDanfe(id);
    setLoading(false);
    if (!result.isAxiosError) {
      openAbaDanfeBase64(result.data);
    } else {
      responseErros(result);
    }
  };

  const onClickBaixarXml = async () => {
    setLoading(true);
    const result = await mdfeService.getArquivoXml(id);
    if (!result.isAxiosError) {
      const blob = new Blob([result.data], { type: "text/xml" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      const fileName = mdfe?.chaveDfe || mdfe?.chaveAcesso || `mdfe-${id}`;
      a.download = `${fileName}.xml`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      responseErros(result);
    }
    setLoading(false);
  };

  async function openAbaDanfeBase64(base64Pdf) {
    try {
      const response = await fetch(`data:application/pdf;base64,${base64Pdf}`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const pdfWindow = window.open(url, "_blank");
      if (!pdfWindow) {
        notification.alertaGenericos(
          "Popup bloqueado - por favor permita popups para este site"
        );
      }

      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      console.error("Erro ao abrir PDF:", error);
      throw error;
    }
  }

  const onSearchChangeVeiculo = async (event, inputRef) => {
    if (event.key === "Enter" && inputRef.current === document.activeElement) {
      const filtros = {
        placa: event.target?.value.replaceAll("-", ""),
        renavan: event.target?.value,
        nonPaginated: true,
        restritiva: true,
        tipo: "VEICULO",
      };
      setLoadingAutoComplete(true);
      await buscarVeiculos(filtros);
      setLoadingAutoComplete(false);
    }
  };

  const onChangeAutocompleteVeiculo = (value) => {
    setInputErro([]);
    setMdfe({
      ...mdfe,
      veiculo: value,
      fatVeiculoId: value ? value.id : undefined,
    });
  };

  const handleInputChange = (event) => {
    setInputErro([]);
    setMdfe({
      ...mdfe,
      [event.target.name]: event.target.value || "",
    });
  };

  const emitirAposCadastro = async (data) => {
    const props = {
      title: "Documento cadastrado com sucesso",
      text: `#${data.numeroDocumento}`,
      icon: "success",
      confirmButtonText: "EMITIR",
      cancelButtonText: "FECHAR",
      showCancelButton: true,
    };
    const confirmacaoAlerta = await notification.confirmacaoGenericos(props);
    if (confirmacaoAlerta.isConfirmed) {
      const notaEmitida = await emitirMdfe(data.id);
      if (notaEmitida) {
        history.push(`/faturamento/mdfe/${data.id}`);
      }
    } else {
      initialCollapses();
      voltar();
    }
  };

  function validarCadastro(mdfeEntity, indiceSelecionado) {
    mdfeEntity.setCondutores(condutoresMdfe);
    mdfeEntity.setReboques(reboques);
    mdfeEntity.setNfsSaida(nfsSaida);
    mdfeEntity.setMunicipiosCarregamento(carregamentos);
    mdfeEntity.setPercursos(percursos);
    mdfeEntity.setLacres(lacres);
    mdfeEntity.setPedagios(pedagios);
    mdfeEntity.setContratantes(contratantes);
    mdfeEntity.setCiots(ciots);
    mdfeValidator
      .validate(mdfeEntity, { abortEarly: false })
      .then(async () => {
        const response = await mdfeService.cadastrar(mdfeEntity);
        setLoading(false);
        if (!response.isAxiosError) {
          notification.cadastroSucesso();
          if (indiceSelecionado === 0) {
            emitirAposCadastro(response.data);
          } else {
            limparFormularios();
          }
        } else {
          responseErros(response);
        }
      })
      .catch(async (err) => {
        setLoading(false);
        if (err.inner[0].path == "condutores") setAbaValue(0);
        if (err.inner[0].path == "municipiosCarregamento") setAbaValue(1);
        if (err.inner[0].path == "nfsSaida") setAbaValue(3);
        inputErros.set(err);
      });
  }

  const limparFormularios = () => {
    inputErros.limpar();
    setAbaValue(0);
    setMdfe({});
    setPercursos([]);
    setCarregamentos([]);
    setNfsSaida([]);
    setCondutoresMdfe([]);
    setReboques([]);
  };

  async function validarAtualizacao(mdfeEntity) {
    mdfeUpdateValidator
      .validate(mdfeEntity, { abortEarly: false })
      .then(async () => {
        const response = await mdfeService.atualizar(id, mdfeEntity);
        setLoading(false);
        if (!response.isAxiosError) {
          notification.alteracaoSucesso();
          initialCollapses();
          voltar();
        } else {
          responseErros(response);
        }
      })
      .catch(async (err) => {
        setLoading(false);
        inputErros.set(err);
      });
  }

  async function handleSubmit(indiceSelecionado) {
    setLoading(true);
    const mdfeEntity = new MdfeEntity(mdfe);
    await mdfeEntity.setProprietarioVeiculo(mdfe.veiculo);
    if (id) {
      validarAtualizacao(mdfeEntity);
    } else {
      validarCadastro(mdfeEntity, indiceSelecionado);
    }
  }

  const valueDataEmisao = (data) => {
    if (!data) {
      return "";
    }
    return format(formatDate.received(data), "dd/MM/yyyy");
  };

  const onClickVoltar = () => {
    history.push(`/faturamento/mdfe`);
  };

  const disabledCampos = () => {
    if (!mdfe?.status) {
      return false;
    }
    if (mdfe.status != "PENDENTE") {
      return true;
    }
    return false;
  };

  const showUpdate = () => {
    const temPermisao = permissoesHelper.temPermisao("mdfe-editar");
    if (!temPermisao) {
      return false;
    }
    return mdfe?.status != "PENDENTE" ? false : true;
  };

  return (
    <AddLayout
      id={id}
      onClickSalvar={handleSubmit}
      onClickVoltar={onClickVoltar}
      title={id ? "Editar MDFE" : "Cadastrar MDFE"}
      loading={loading}
      onClickLog={onClickLog}
      userLog={id ? userLog : null}
      showUpdate={showUpdate()}
      ButtonMenu={
        id ? (
          <ButtonMenu
            onClickCancelarMdfe={onClickCancelarMdfe}
            onClickEmitirMdfe={onClickEmitirMdfe}
            onClickVisualizarMdfe={onClickVisualizarMdfe}
            onClickBaixarXml={onClickBaixarXml}
            onClickEncerrarMdfe={onClickEncerrarMdfe}
            status={mdfe.status}
          />
        ) : null
      }
    >
      <Grid container spacing={2}>
        <Grid item xs={3}>
          <Autocomplete
            id="fatVeiculoId"
            name="fatVeiculoId"
            options={veiculos}
            autoHighlight
            disabled={disabledCampos()}
            getOptionLabel={(option) => formatPlacaVeiculo(option.placa)}
            filterSelectedOptions
            filterOptions={(options, state) => {
              const input = state.inputValue.replaceAll("-", "").toUpperCase();
              return options.filter((item) =>
                item.placa.toUpperCase().includes(input)
              );
            }}
            loading={loadingAutoComplete}
            loadingText="Carregando"
            noOptionsText={
              document.getElementById("fatVeiculoId")?.value.length >= 1 &&
              !id ? (
                <Grid container spacing={1}>
                  <Grid item xs={12} fullWidth>
                    Nenhum veiculo encontrado
                  </Grid>
                </Grid>
              ) : (
                "Digite e pressione Enter"
              )
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Veiculo"
                error={inputErros.get("fatVeiculoId")}
                required
                onKeyDown={(e) => onSearchChangeVeiculo(e, inputVeiculos)}
                inputRef={inputVeiculos}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loadingAutoComplete ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
            onChange={(_, newValue) => onChangeAutocompleteVeiculo(newValue)}
            value={mdfe?.veiculo ?? ""}
          />
        </Grid>
        <Grid item xs={1}>
          <TextField
            id="serieDocumento"
            name="serieDocumento"
            label="Série"
            type="number"
            disabled={id ? true : false}
            error={inputErros?.get("serieDocumento")}
            onChange={handleInputChange}
            value={value.text(mdfe.serieDocumento)}
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={2}>
          <TextField
            id="numeroDocumento"
            name="numeroDocumento"
            label="Nº Documento"
            type="text"
            disabled
            fullWidth
            value={value.text(mdfe?.numeroDocumento)}
          />
        </Grid>
        <Grid item xs={2}>
          <TextField
            id="modelo"
            name="modelo"
            label="Modelo Documento"
            type="text"
            disabled
            fullWidth
            value="58"
          />
        </Grid>
        <Grid item xs={2}>
          <TextField
            id="dataEmisao"
            name="dataEmissao"
            label="Data Emissão"
            type="text"
            disabled
            fullWidth
            value={valueDataEmisao(mdfe?.dataEmissao)}
          />
        </Grid>
        <Grid item xs={2}>
          <TextField
            id="status"
            name="status"
            label="Status"
            variant="outlined"
            disabled
            value={mdfe.status ?? ""}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {verificarStatus(mdfe?.status)}
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={2}>
          <TextField
            id="pesoTotal"
            name="pesoTotal"
            label="Peso Total MDF-e"
            type="number"
            disabled
            margin="normal"
            onChange={handleInputChange}
            fullWidth
            value={value.text(pesoTotal)}
          />
        </Grid>
        <Grid item xs={2}>
          <CurrencyInput
            id="valorTotalMdfe"
            name="valorTotalMdfe"
            label="Valor Total MDF-e"
            value={valorTotal}
            disabled
          />
        </Grid>
      </Grid>
      <div className="mt-4">
        <TabsMdfe
          abaValue={abaValue}
          setAbaValue={setAbaValue}
          setCondutoresMdfe={setCondutoresMdfe}
          condutoresMdfe={condutoresMdfe}
          percursos={percursos}
          setPercursos={setPercursos}
          estados={estados}
          carregamentos={carregamentos}
          setCarregamentos={setCarregamentos}
          nfsSaida={nfsSaida}
          setNfsSaida={setNfsSaida}
          reboques={reboques}
          setReboques={setReboques}
          id={id}
          setLoading={setLoading}
          montarNfsSaidas={montarNfsSaidas}
          montarReboques={montarReboques}
          status={mdfe?.status || "PENDENTE"}
          calcularTotaisNfsSaida={calcularTotaisNfsSaida}
          calcularValorTotalNfSaida={calcularValorTotalNfSaida}
          lacres={lacres}
          setLacres={setLacres}
          pedagios={pedagios}
          setPedagios={setPedagios}
          contratantes={contratantes}
          setContratantes={setContratantes}
          ciots={ciots}
          setCiots={setCiots}
        />
      </div>
      <Divider sx={{ mt: 3, border: "1px solid grey", width: "100%" }} />
      <div className="mt-3">
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <span className="fw-bold fw-bolder">Informações adicionais</span>
          </Grid>
          <Grid item xs={12}>
            <TextareaAutosize
              className="w-100 p-0.5"
              aria-label="minimum height"
              name="informacoesAdicionais"
              id="informacoesAdicionais"
              disabled={disabledCampos()}
              onChange={handleInputChange}
              value={mdfe?.informacoesAdicionais ?? ""}
              minRows={5}
              placeholder="Informações Adicionais"
            />
          </Grid>
        </Grid>
      </div>
      <ErrosMdfeDialog
        open={errosMdfeDialogOpen}
        setOpen={setErrosMdfeDialogOpen}
        errosMdfe={errosMdfe}
        id={id}
      />
      <CancelamentoDialog
        open={openCancelamentoDialog}
        setOpen={setOpenCancelamentoDialog}
        setLoading={setLoading}
        id={id}
        buscarMdfe={buscarMdfe}
      />
    </AddLayout>
  );
};

export default AddMdfeView;
